import React, { useCallback, useEffect, useState } from 'react';
import { Card, Footer } from '../../common';
import { EmptyState } from '../../../../../commons';
import { InvalidSearchImg } from '../../../../../../images/images';
import {
  Textarea,
  Heading,
  NestedRadio,
  SubHeading,
  CheckboxList,
  Input,
} from '../../../../../../UiKit';
import {
  getFormData,
  checkIfOptional,
  checkRadioValidations,
  getSelectedRadioOptions,
  getSubmitBtnTitle,
  getStickyTitle,
} from '../../../../../commons/CommonMethods';
import { reasonModel } from '../../../../../../api';
import toast from 'react-hot-toast';
import {
  MetricsForJumboEvent,
  RFO_CACR_REASON_ID,
} from '../../../../../../analytics/constants';
import { VerifyAddressBtnClickEvent } from '../../../../../../analytics/raise-an-issue/events';
import {
  RfoCancellationReasonSelectedEvent,
  RfoEvaluateResponseEvent,
} from '../../../../../../analytics/rfo/events';
import { VerifyAddressServiceability } from './AddressServiceability/VerifyAddressServiceability';
import { AddressServiceabilityResponseUI } from './AddressServiceability/AddressServiceabilityResponseUI';

export const CreditDetails = ({
  creditOptions = null,
  isDisabled = false,
  reasonId = null,
  orderId = null,
  handleCardLink = () => {},
  sendFormData = () => {},
  cartId = null,
  userId = null,
}) => {
  const [comment, setComment] = useState('');
  const [commentErrorText, setCommentErrorText] = useState('');
  const [isEnabled, setIsEnabled] = useState(
    !!creditOptions?.submit_button?.is_enabled,
  );
  const [stickyTitle, setStickyTitle] = useState('');
  const [submitBtnTitle, setSubmitBtnTitle] = useState('');
  const [refundValue, setRefundValue] = useState(null);
  const [checkBox, setCheckBox] = useState(null);
  const [selectedCheckboxOption, setSelectedCheckboxOption] = useState(null);
  const [reasonInputVal, setReasonInputVal] = useState(null);
  const [reasonInputErrorText, setReasonInputErrorText] = useState('');
  const [updatedRadio, setUpdatedRadio] = useState(null);
  const [refundOptions, setRefundOptions] = useState();
  const [selectedRefundOption, setSelectedRefundOption] = useState(null);
  const [showVerifyAddressBtn, setShowVerifyAddressBtn] = useState(false);
  const [openVerifyAddressModal, setOpenVerifyAddressModal] = useState(false);
  const [addressServiceability, setAddressServiceability] = useState(null);
  const [serviceabilityError, setServiceabilityError] = useState(null);

  useEffect(() => {
    refundOptions?.radio?.source.forEach((option) => {
      if (option?.selected) {
        const optionSelected = {
          [refundOptions?.radio?.input?.key]: option?.value,
        };
        setSelectedRefundOption(optionSelected);
        setIsEnabled(true);
      }
    });
  }, [refundOptions]);

  const submitForm = useCallback(() => {
    const { submit_button, comment_box, input_box } = creditOptions;

    const { url } = submit_button;
    let data = {},
      radioData = {},
      checkboxData = {};

    if (!isEnabled) {
      return;
    }

    if (!checkIfOptional(input_box, reasonInputVal)) {
      setReasonInputErrorText('This field is mandatory');
      return;
    }

    if (!checkIfOptional(comment_box, comment)) {
      setCommentErrorText('This field is mandatory');
      return;
    }

    if (checkBox) {
      checkboxData = getFormData(checkBox, selectedCheckboxOption);
    }

    if (updatedRadio) {
      radioData = getSelectedRadioOptions(updatedRadio);
    }

    const commentsData = getFormData(comment_box, comment);
    const reasonInputData = getFormData(input_box, reasonInputVal);

    Object.assign(
      data,
      commentsData,
      refundValue,
      radioData,
      checkboxData,
      reasonInputData,
      selectedRefundOption,
      submit_button?.submit_button_object || {},
    );
    data['order_id'] = orderId;
    sendFormData(url, data);
  }, [
    isEnabled,
    checkBox,
    comment,
    orderId,
    reasonInputVal,
    creditOptions,
    refundValue,
    selectedCheckboxOption,
    updatedRadio,
    refundOptions,
    selectedRefundOption,
    sendFormData,
  ]);

  const isRfoCacrFlow = Number(reasonId) === RFO_CACR_REASON_ID;

  const handleRadioChange = useCallback(
    (item, data) => {
      setUpdatedRadio(data);

      if (isRfoCacrFlow) {
        RfoCancellationReasonSelectedEvent({
          [MetricsForJumboEvent.order_id]: orderId,
          [MetricsForJumboEvent.cart_id]: cartId,
          [MetricsForJumboEvent.customer_id]: userId,
          [MetricsForJumboEvent.feature_meta]: {
            reason_text: item?.text,
            reason_value: item?.value,
          },
        });
      }

      const isRadiovalid = checkRadioValidations(data);
      !item.radio &&
        creditOptions &&
        setSubmitBtnTitle(
          getSubmitBtnTitle(creditOptions.submit_button, data.selectedValue),
        );
      !item.radio &&
        creditOptions &&
        setStickyTitle(getStickyTitle(creditOptions.toast, data.selectedValue));
      setRefundValue(item.refund_value);
      setCheckBox(item.check_box);

      if (!item.url) {
        setIsEnabled(!!isRadiovalid && !item.check_box);
        return;
      }

      if (item.verify_address) {
        setShowVerifyAddressBtn(true);
      } else {
        setShowVerifyAddressBtn(false);
      }
      setAddressServiceability(null);
      setServiceabilityError(null);

      reasonModel
        .getReundOptionInCancelOrderFlow(
          reasonId,
          item.url,
          orderId,
          data.input.key,
          item.value,
        )
        .then((res) => {
          if (isRfoCacrFlow) {
            RfoEvaluateResponseEvent({
              [MetricsForJumboEvent.order_id]: orderId,
              [MetricsForJumboEvent.cart_id]: cartId,
              [MetricsForJumboEvent.customer_id]: userId,
              [MetricsForJumboEvent.feature_meta]: {
                request: JSON.stringify({
                  slug: item.url,
                  [data.input.key]: item.value,
                }),
                success: !res?.isError,
                error: res?.isError ? res?.message : undefined,
                meta_info: res?.meta_info?.text,
                refund_options: JSON.stringify(res?.radio?.source),
              },
            });
          }
          if (res?.isError) {
            return toast.error(res?.message);
          }
          setRefundOptions(res);
          const refundCheck = res?.radio?.source?.length
            ? selectedRefundOption
            : true;
          setIsEnabled(!!isRadiovalid && !item.check_box && refundCheck);
        })
        .catch((err) => {
          toast.error(err?.message);
          return;
        });
    },
    [
      creditOptions,
      selectedRefundOption,
      isRfoCacrFlow,
      orderId,
      cartId,
      userId,
      reasonId,
    ],
  );

  const onRadioInputChange = useCallback(
    (value) => {
      const isRadiovalid = checkRadioValidations(updatedRadio);
      setIsEnabled(!!value?.length && !!isRadiovalid);
    },
    [updatedRadio],
  );

  const selectCheckboxOptions = useCallback(
    (selectedOptions) => {
      const checkedOption =
        selectedOptions?.length === checkBox?.source?.length;
      setIsEnabled(checkedOption);
      setSelectedCheckboxOption(selectedCheckboxOption);
    },
    [checkBox, selectedCheckboxOption],
  );

  const handleRefundOptionChange = useCallback(
    (item, data) => {
      const optionSelected = { [data?.input?.key]: item?.value };
      setSelectedRefundOption(optionSelected);
      setIsEnabled(true);
    },
    [refundOptions, selectedRefundOption],
  );

  if (!creditOptions) return null;

  let btnBgColor = !isEnabled ? '#B5B5B5' : '#4B9B42';

  const handleVerifyAddressBtnClick = () => {
    VerifyAddressBtnClickEvent({
      [MetricsForJumboEvent.order_id]: orderId,
      [MetricsForJumboEvent.cart_id]: cartId,
      [MetricsForJumboEvent.customer_id]: userId,
      [MetricsForJumboEvent.feature_meta]: {
        selected_cancellation_reason: updatedRadio?.selectedValue,
      },
    });
    setOpenVerifyAddressModal(true);
  };

  const handleVerifyAddressClose = () => {
    setOpenVerifyAddressModal(false);
  };

  const updateAddressServiceability = ({
    isServiceable,
    distance = null,
    reason = null,
    error = null,
  }) => {
    if (error) {
      setServiceabilityError(error);
      setAddressServiceability(null);
    } else {
      setAddressServiceability({
        isServiceable: isServiceable,
        distance: distance,
        reason: reason,
      });
    }
    setShowVerifyAddressBtn(false);
  };

  const isFooterDisabled =
    isDisabled ||
    showVerifyAddressBtn ||
    addressServiceability?.isServiceable ||
    (serviceabilityError?.isError &&
      serviceabilityError.data?.curr_merchant_serviceable);

  return (
    <React.Fragment>
      {creditOptions.title && <Heading title={creditOptions.title.text} />}
      {creditOptions.subtitle && (
        <SubHeading title={creditOptions.subtitle.text} />
      )}

      {creditOptions.card && (
        <div className='pt10 pb10'>
          <Card
            content={creditOptions.card}
            onClick={() => handleCardLink(creditOptions.card)}
          />
        </div>
      )}

      {creditOptions?.meta_info?.text && (
        <div className='tw-text-red-500 tw-text-[14px] tw-font-medium tw-py-2'>
          {creditOptions.meta_info.text}
        </div>
      )}

      {creditOptions?.radio?.source?.length > 0 ? (
        <React.Fragment>
          <div>
            {creditOptions.radio.title && (
              <Heading title={creditOptions.radio.title} />
            )}
            <NestedRadio
              data={creditOptions.radio}
              items={creditOptions.radio.source}
              selectOption={handleRadioChange}
              onRadioInputChange={onRadioInputChange}
            />
          </div>
        </React.Fragment>
      ) : creditOptions?.skip_radio_check ? null : (
        <EmptyState
          img={InvalidSearchImg}
          heading={'No reason options found'}
          subHeading={'Please select another reason type'}
          isPop={true}
        />
      )}

      {refundOptions?.radio?.source?.length ? (
        <React.Fragment>
          <div>
            {refundOptions.radio.title && (
              <Heading title={refundOptions.radio.title} />
            )}
            {/* Cancellation fee comm */}
            {refundOptions?.refund_options_title?.text ? (
              <div className='tw-text-[#e23744] tw-text-[14px] tw-font-medium'>
                {refundOptions.refund_options_title.text}
              </div>
            ) : null}
            <NestedRadio
              data={refundOptions.radio}
              items={refundOptions.radio.source}
              selectOption={handleRefundOptionChange}
            />
          </div>
        </React.Fragment>
      ) : refundOptions?.meta_info?.text ? (
        <Heading title={refundOptions?.meta_info?.text} />
      ) : null}

      {/* Input box */}
      {creditOptions.input_box && (
        <React.Fragment>
          {creditOptions.input_box.title && (
            <Heading title={creditOptions.input_box.title.text} />
          )}
          <Input
            placeholder={creditOptions.input_box.placeholder}
            value={reasonInputVal}
            type={creditOptions.input_box.format}
            onChange={(value) => setReasonInputVal(value)}
            error={!!reasonInputErrorText.length}
            errorText={reasonInputErrorText}
          />
        </React.Fragment>
      )}

      <AddressServiceabilityResponseUI
        addressServiceability={addressServiceability}
      />

      <VerifyAddressServiceability
        openVerifyAddressModal={openVerifyAddressModal}
        handleVerifyAddressClose={handleVerifyAddressClose}
        orderId={orderId}
        updateAddressServiceability={updateAddressServiceability}
        showVerifyAddressBtn={showVerifyAddressBtn}
        handleVerifyAddressBtnClick={handleVerifyAddressBtnClick}
      />

      {/* comment box */}
      {creditOptions?.radio?.source?.length > 0 &&
        creditOptions.comment_box && (
          <div className='pt10'>
            <Textarea
              showBtn={false}
              placeholder={creditOptions.comment_box.placeholder}
              height='15vh'
              onChange={(event) => setComment(event.target.value)}
              error={!!commentErrorText.length}
              errorText={commentErrorText}
            />
          </div>
        )}

      {/* checkbox list */}
      {checkBox && checkBox.source.length && (
        <div className='pt20'>
          {checkBox.title && <Heading title={checkBox.title.text} />}
          {checkBox.subtitle && <SubHeading title={checkBox.subtitle.text} />}
          <CheckboxList
            items={checkBox.source}
            selectOptions={selectCheckboxOptions}
          />
        </div>
      )}

      <Footer
        issueInfo={creditOptions}
        submitBtnTitle={submitBtnTitle}
        stickyTitle={stickyTitle}
        btnBgColor={btnBgColor}
        sendFormData={submitForm}
        isDisabled={isFooterDisabled}
      />
    </React.Fragment>
  );
};
